// src/Pages/Onboarding/OnboardingGate.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import { useAuth } from "../../context/AuthContext.jsx";
import { api } from "../../services/api.js";
import { nav as routeNav } from "../../utils/nav.js";
import { PageOverlayLoading } from "../../components/Loading/Spinner.jsx";

// Pidä nämä linjassa todellisten reittien kanssa
const PATH_TERMS = "/kayttoehdot";
const PATH_PRIVACY = "/tietosuoja";
const TERMS_VERSION = "2025-01-01";

export default function OnboardingGate() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();

  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // Mitä on jo hyväksytty?
  const already = useMemo(
    () => ({
      terms: Boolean(user?.accepted_terms_at),
      privacy: Boolean(user?.accepted_privacy_at),
    }),
    [user]
  );

  // MÄÄRITÄ TÄHÄN SOVELLUKSEN OIKEA LOGIN-POLKU
  const PATH_LOGIN = "/kirjaudu"; // jos teillä on eri, vaihda tähän

  useEffect(() => {
    if (!loading && !user) {
      navigate(`${PATH_LOGIN}?next=/onboarding`, { replace: true });
    }
  }, [loading, user, navigate, PATH_LOGIN]);

useEffect(() => {
  if (!user) return;
  const hasAccepted = already.terms && already.privacy;
  if (!hasAccepted) return;  // jää tälle sivulle hyväksymään
  try { sessionStorage.setItem("dashboard.skipSetupOnce", "1"); } catch {}
  routeNav.toDashboardUser(navigate, true); // → /dashboard?band=user&tab=uprofile
}, [user, already.terms, already.privacy, navigate]);


  // Saako jatkaa?
  const canContinue = useMemo(() => {
    const termsOk = already.terms || agreeTerms;
    const privacyOk = already.privacy || agreePrivacy;
    return termsOk && privacyOk && !submitting;
  }, [already, agreeTerms, agreePrivacy, submitting]);

  const onAccept = async () => {
    if (submitting) return; // estä tuplaklikki
    if (!user) return;
    setErr("");
    setSubmitting(true);
    try {
      const payload = {
        terms: !already.terms && agreeTerms,
        privacy: !already.privacy && agreePrivacy,
        version: TERMS_VERSION, // valinnainen versiointi auditointia varten
      };

      // Jos kaikki oli jo hyväksytty, ohita API-kutsu ja reititä
      if (!payload.terms && !payload.privacy) {
        try { sessionStorage.setItem("dashboard.skipSetupOnce", "1"); } catch {}
        routeNav.toDashboardUser(navigate, true);
        return;
      }

      await api.auth.accept(payload); // POST /api/auth/accept

      // Päivitä konteksti ja päätä mihin mennään
      try {
        await refresh?.();
      } catch {}

      try { sessionStorage.setItem("dashboard.skipSetupOnce", "1"); } catch {}
      routeNav.toDashboardUser(navigate, true);
    } catch (e) {
      setErr(e?.message || "Hyväksyntä epäonnistui. Yritä uudelleen.");
    } finally {
      setSubmitting(false);
    }
  };

  // Lataus/ohjaus-gate:
  // - jos auth latautuu -> spinner
  // - jos auth valmis eikä käyttäjää -> redirect-effect hoitaa, ei renderöidä mitään
  if (loading) return <PageOverlayLoading label="Ladataan…" />;
  if (!user) return null;

  const labelText =
    "flex-1 dark:text-white/80 text-black/80 font-customFont1 " +
    "text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm";

  return (
    <div className="min-h-screen bg-lemon/60 dark:bg-black/40 pt-12">
      {/* Mobiilissa ylhäältä; kaikissa desktop-breakpointeissa keskelle */}
      <div
        className={[
          "mx-auto max-w-6xl px-4 pb-12",
          "pt-24", // mobile/top
          "md:min-h-[calc(100vh-96px)] md:grid md:place-items-center",
        ].join(" ")}
      >
        <div className="w-full md:max-w-4xl lg:max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-left md:text-center mb-4">
            <h1 className="text-2xl font-bold text-black/90 dark:text-white/95">
              Kiitos rekisteröitymisestä!
            </h1>
            <p className="mt-1 text-xs md:text-lg text-black/70 dark:text-white/80 font-medium">
              Tilisi on valmis🎉 Seuraavaksi lue käyttöehdot, viimeistele profiili ja tee ensimmäinen esiintyjäkorttisi.
            </p>
          </div>

          {/* 2-saraketta kaikissa desktop-breakpointeissa */}
          <div
            className={[
              "grid grid-cols-1 gap-6",
              "md:grid-cols-2 md:items-start md:justify-center"
            ].join(" ")}
          >
            {/* Oikea palsta (mobiilissa alempana) */}
            <div className="order-2 md:order-2">
              <div className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/60 backdrop-blur-md shadow-lg w-full p-4 sm:p-5">
                {/* Käyttöehdot */}
                <section className="rounded-lg ring-1 ring-black/10 dark:ring-white/10 bg-white/80 dark:bg-black/50 p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <input
                      id="chk-terms"
                      type="checkbox"
                      className="mt-1"
                      checked={already.terms ? true : agreeTerms}
                      disabled={already.terms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                    />
                    <label htmlFor="chk-terms" className={labelText}>
                      Olen lukenut ja hyväksyn{" "}
                      <Link
                        to={{ pathname: PATH_TERMS, search: "?from=onboarding" }}
                        state={{ fromOnboarding: true }}
                        className="underline text-pink dark:text-primary"
                      >
                        käyttöehdot
                      </Link>
                      .
                      {already.terms && (
                        <span className="ml-2 text-xs opacity-70">
                          (hyväksytty aiemmin ✅)
                        </span>
                      )}
                    </label>
                  </div>
                </section>

                {/* Tietosuoja */}
                <section className="rounded-lg ring-1 ring-black/10 dark:ring-white/10 bg-white/80 dark:bg-black/50 p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <input
                      id="chk-privacy"
                      type="checkbox"
                      className="mt-1"
                      checked={already.privacy ? true : agreePrivacy}
                      disabled={already.privacy}
                      onChange={(e) => setAgreePrivacy(e.target.checked)}
                    />
                    <label htmlFor="chk-privacy" className={labelText}>
                      Olen lukenut ja hyväksyn{" "}
                      <Link
                        to={{ pathname: PATH_PRIVACY, search: "?from=onboarding" }}
                        state={{ fromOnboarding: true }}
                        // ei lisättyjä/ylimääräisiä linkkejä muualla sivulla
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-pink dark:text-primary"
                      >
                        tietosuojaselosteen
                      </Link>
                      .
                      {already.privacy && (
                        <span className="ml-2 text-xs opacity-70">
                          (hyväksytty aiemmin ✅)
                        </span>
                      )}
                    </label>
                  </div>
                </section>

                {/* Virheviesti */}
                {err && (
                  <div
                    className="rounded-md bg-red-100/70 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 px-3 py-2 text-sm text-red-700 dark:text-red-300 mb-4"
                    aria-live="polite"
                  >
                    {err}
                  </div>
                )}

                {/* Toiminnot */}
                <div className="flex items-center gap-3">
                  <NeonButton
                    variant="primary"
                    className="scale-[0.80] md:scale-100 font-semibold"
                    size="sm"
                    disabled={!canContinue}
                    onClick={onAccept}
                  >
                    {submitting ? "Tallennetaan…" : "Hyväksy ja jatka"}
                  </NeonButton>
                </div>
              </div>
            </div>

            {/* Vasen palsta: tekstit ilman ylimääräisiä linkkejä */}
            <div className="order-1 md:order-1">
              <div className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/50 backdrop-blur p-4">
                {/* onnistui-banneri */}
                {/* <div className="mb-3 rounded-lg border border-emerald-300/60 dark:border-emerald-700/60 bg-emerald-100/70 dark:bg-emerald-900/30 p-3">
                  <div className="flex items-start gap-3">
                    <span aria-hidden className="mt-0.5">✅</span>
                    <p className="text-black/80 dark:text-white/85 text-sm leading-relaxed">
                      Tili on luotu. Hyväksymällä ehdot vahvistat sopimuksen ja pääset
                      jatkamaan.
                    </p>
                  </div>
                </div> */}

                <h2 className="font-semibold text-black/90 dark:text-white/90 mb-2 text-2xl">
                  Käyttöehdot pähkinänkuoressa
                </h2>
               <ul className="list-disc pl-5 space-y-1 text-black/80 dark:text-white/80 text-xs md:text-lg md:text-emerald-500">
                    {/* Tiivistetty kieli + tarkennettu vastuut */}
                    <li>
                      Käytät palvelua asiallisesti ja pidät tietosi (kalenteri, hinnat, yhteystiedot) ajan tasalla.
                    </li>
                    <li>
                      Noudatat Buukkerin käyttöehtoja ja ohjeita.
                    </li>
                    <li>
                      Kunnioitat tekijänoikeuksia ja yksityisyyttä – julkaiset vain materiaalia, johon sinulla on oikeus.
                    </li>
                    <li>
                      Maksulliset ominaisuudet, varaukset ja peruutukset hoidat käyttöehtojen mukaisesti.
                    </li>
                    <li>
                      <span className="font-semibold">Tärkeintä:</span> jos merkitset itsesi buukattavaksi, sitoudut hoitamaan sovitut keikat.
                    </li>
                  </ul>

             <h2 className="font-semibold text-black/90 dark:text-white/90 mt-4 mb-1 text-2xl">
              Tietosuojan tiivistelmä
            </h2>
              <p className="text-black/80 dark:text-white/80 leading-relaxed text-xs md:text-lg">
                Seloste tiivistää, mitä tietoja keräämme (tili- ja yhteystiedot, viestintä- ja lokitiedot), 
                mihin niitä käytämme (tilin ylläpito, turvallisuus, analytiikka), kuinka kauan niitä säilytämme 
                ja mitkä ovat oikeutesi (tarkastaa, oikaista, poistaa).
              </p>

            <p className="mt-4 text-black/80 dark:text-white/80 leading-relaxed text-xs md:text-lg">
              Kun hyväksyt kohdat, siirryt täyttämään käyttäjätietosi loppuun.
              Tarvitsetko apua? Pikaohjeet löydät{" "}
              <Link
                to="/ukk-esiintyjille"
                className="font-semibold underline text-pink dark:text-primary  hover:opacity-90"
              >
                Esiintyjien UKK -sivulta
              </Link>.
            </p>


                {/* “En suostu” -tilanne: kepeä teksti + tooltip */}
                <div className="mt-4 flex justify-center">
                  <NeonButton
                    as={Link}
                    to="/"
                    variant="primary"
                    className="scale-[0.80] md:scale-100 font-semibold"
                    size="sm"
                    title="Ei sopparia tänään? Ei hätää — voit palata milloin vain."
                    aria-label="Palaa etusivulle (ei sopparia tänään)"
                  >
                    <span className="inline-flex items-center gap-2">
                      <span aria-hidden>🫣</span>
                      <span>Ei tänään — vie minut etusivulle</span>
                    </span>
                  </NeonButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>{" "}
    </div>
  );
}
